import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Loader2, Sparkles, ShieldCheck, RefreshCw, XCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CocoonMark } from "@/components/CocoonMark";

const T = {
  fr: { title: "Mon abonnement", subtitle: "Gère ton forfait et tes paiements en toute liberté.", back: "Retour",
    currentPlan: "Ton forfait actuel", free: "Le Souffle (gratuit)", elan: "L'Élan", cocon: "Le Cocoon",
    status: "Statut", active: "Actif", canceling: "Se termine bientôt", incomplete: "Paiement en attente",
    renews: "Prochain renouvellement le", ends: "Accès jusqu'au", price: "Montant",
    perMonth: "par mois", perYear: "par an",
    cancel: "Annuler l'abonnement", cancelTitle: "Annuler ton abonnement ?",
    cancelDesc: "Tu gardes l'accès jusqu'à la fin de la période déjà payée. Ensuite, ton compte repasse au Souffle (gratuit).",
    cancelOk: "Oui, annuler", cancelNo: "Garder mon abonnement", canceled: "Ton abonnement sera annulé à la fin de la période.",
    resume: "Réactiver l'abonnement", resumed: "Abonnement réactivé, merci !",
    noSub: "Tu n'as pas d'abonnement payant pour l'instant.", upgrade: "Découvrir les forfaits",
    rewardNote: "Accès Cocoon offert grâce au parrainage — aucun paiement en cours.",
    adminNote: "Compte administrateur — accès illimité, sans abonnement.",
    updateCard: "Mettre à jour ma carte", portalError: "Le portail de facturation n'est pas disponible pour le moment.",
    managePortal: "Gérer mon abonnement", portalHint: "Ouvre ton espace de facturation Stripe sécurisé pour changer de formule, mettre à jour ta carte, télécharger tes factures ou annuler — en toute autonomie.", portalOpen: "Ouvrir mon espace Stripe",
    error: "Une erreur est survenue.", secure: "Paiements sécurisés par Stripe" },
  en: { title: "My subscription", subtitle: "Manage your plan and payments freely.", back: "Back",
    currentPlan: "Your current plan", free: "The Breath (free)", elan: "The Momentum", cocon: "The Cocoon",
    status: "Status", active: "Active", canceling: "Ending soon", incomplete: "Payment pending",
    renews: "Next renewal on", ends: "Access until", price: "Amount",
    perMonth: "per month", perYear: "per year",
    cancel: "Cancel subscription", cancelTitle: "Cancel your subscription?",
    cancelDesc: "You keep access until the end of the period already paid. After that, your account returns to The Breath (free).",
    cancelOk: "Yes, cancel", cancelNo: "Keep my subscription", canceled: "Your subscription will be cancelled at the end of the period.",
    resume: "Resume subscription", resumed: "Subscription resumed, thank you!",
    noSub: "You don't have a paid subscription yet.", upgrade: "See the plans",
    rewardNote: "Cocoon access offered through referral — no payment in progress.",
    adminNote: "Administrator account — unlimited access, no subscription.",
    updateCard: "Update my card", portalError: "The billing portal is unavailable right now.",
    managePortal: "Manage my subscription", portalHint: "Open your secure Stripe billing space to change plan, update your card, download invoices or cancel — all on your own.", portalOpen: "Open my Stripe space",
    error: "Something went wrong.", secure: "Payments secured by Stripe" },
  es: { title: "Mi suscripción", subtitle: "Gestiona tu plan y tus pagos con libertad.", back: "Volver",
    currentPlan: "Tu plan actual", free: "El Aliento (gratis)", elan: "El Impulso", cocon: "El Cocoon",
    status: "Estado", active: "Activo", canceling: "Termina pronto", incomplete: "Pago pendiente",
    renews: "Próxima renovación el", ends: "Acceso hasta el", price: "Importe",
    perMonth: "al mes", perYear: "al año",
    cancel: "Cancelar la suscripción", cancelTitle: "¿Cancelar tu suscripción?",
    cancelDesc: "Conservas el acceso hasta el final del periodo ya pagado. Después, tu cuenta vuelve al Aliento (gratis).",
    cancelOk: "Sí, cancelar", cancelNo: "Mantener mi suscripción", canceled: "Tu suscripción se cancelará al final del periodo.",
    resume: "Reactivar la suscripción", resumed: "¡Suscripción reactivada, gracias!",
    noSub: "Aún no tienes una suscripción de pago.", upgrade: "Ver los planes",
    rewardNote: "Acceso Cocoon regalado por recomendación — sin pago en curso.",
    adminNote: "Cuenta de administrador — acceso ilimitado, sin suscripción.",
    updateCard: "Actualizar mi tarjeta", portalError: "El portal de facturación no está disponible ahora.",
    managePortal: "Gestionar mi suscripción", portalHint: "Abre tu espacio de facturación Stripe seguro para cambiar de plan, actualizar tu tarjeta, descargar tus facturas o cancelar — con total autonomía.", portalOpen: "Abrir mi espacio Stripe",
    error: "Ha ocurrido un error.", secure: "Pagos protegidos por Stripe" },
  nl: { title: "Mijn abonnement", subtitle: "Beheer je abonnement en betalingen vrij.", back: "Terug",
    currentPlan: "Je huidige abonnement", free: "De Adem (gratis)", elan: "De Vlucht", cocon: "De Cocoon",
    status: "Status", active: "Actief", canceling: "Eindigt binnenkort", incomplete: "Betaling in behandeling",
    renews: "Volgende verlenging op", ends: "Toegang tot", price: "Bedrag",
    perMonth: "per maand", perYear: "per jaar",
    cancel: "Abonnement opzeggen", cancelTitle: "Je abonnement opzeggen?",
    cancelDesc: "Je behoudt toegang tot het einde van de reeds betaalde periode. Daarna keert je account terug naar De Adem (gratis).",
    cancelOk: "Ja, opzeggen", cancelNo: "Mijn abonnement behouden", canceled: "Je abonnement wordt aan het einde van de periode opgezegd.",
    resume: "Abonnement hervatten", resumed: "Abonnement hervat, bedankt!",
    noSub: "Je hebt nog geen betaald abonnement.", upgrade: "Bekijk de abonnementen",
    rewardNote: "Cocoon-toegang cadeau via doorverwijzing — geen lopende betaling.",
    adminNote: "Beheerdersaccount — onbeperkte toegang, geen abonnement.",
    updateCard: "Mijn kaart bijwerken", portalError: "Het facturatieportaal is momenteel niet beschikbaar.",
    managePortal: "Mijn abonnement beheren", portalHint: "Open je beveiligde Stripe-facturatieruimte om van formule te wisselen, je kaart bij te werken, facturen te downloaden of op te zeggen — volledig zelfstandig.", portalOpen: "Mijn Stripe-ruimte openen",
    error: "Er is iets misgegaan.", secure: "Betalingen beveiligd door Stripe" },
};

const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/eVqeVcdlgfP5egNgDJ3cc00";

export default function Abonnement() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const lang = (i18n.language || "fr").slice(0, 2);
  const L = T[lang] || T.fr;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/subscription");
      setData(data);
    } catch {
      toast.error(L.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const fmtDate = (ts) => {
    if (!ts) return "—";
    try { return new Date(ts * 1000).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }); }
    catch { return "—"; }
  };

  const doCancel = async () => {
    setActing(true);
    try {
      await api.post("/payments/subscription/cancel");
      toast.success(L.canceled);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || L.error);
    } finally { setActing(false); }
  };

  const doResume = async () => {
    setActing(true);
    try {
      await api.post("/payments/subscription/resume");
      toast.success(L.resumed);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || L.error);
    } finally { setActing(false); }
  };

  const doPortal = async () => {
    setActing(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
      else toast.error(L.portalError);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || L.portalError);
    } finally { setActing(false); }
  };

  const planLabel = (p) => (p === "cocon" ? L.cocon : p === "elan" ? L.elan : L.free);
  const sub = data?.subscription;
  const plan = data?.plan || "free";
  const canceling = sub?.cancel_at_period_end;

  return (
    <div className="relative min-h-screen bg-background text-foreground starfield overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "860px", height: "620px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "64%", right: "-14%", left: "auto", width: "600px", height: "600px", opacity: 0.55 }} />
      <header className="glass sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="sub-back-btn"
            onClick={() => navigate("/profil")}
            className="flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={18} /> {L.back}
          </button>
          <span className="font-display text-2xl tracking-tight"><CocoonMark withMind /></span>
        </div>
      </header>

      <motion.section
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 py-14" data-testid="subscription-page"
      >
        <div className="text-center mb-12">
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight mb-4">{L.title}</h1>
          <p className="text-foreground/55 leading-relaxed">{L.subtitle}</p>
        </div>

        {/* Portail client Stripe (no-code) — fonctionne sans backend */}
        <div className="glass rounded-2xl p-6 sm:p-8 mb-6 border border-primary/20 frame-pulse" data-testid="sub-portal-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex-1">
              <p className="flex items-center gap-2 font-display text-2xl text-gradient-orange mb-2">
                <CreditCard size={20} /> {L.managePortal}
              </p>
              <p className="text-foreground/60 text-sm leading-relaxed">{L.portalHint}</p>
            </div>
            <a
              href={STRIPE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sub-stripe-portal-link"
              className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap w-full sm:w-auto"
            >
              <ShieldCheck size={16} /> {L.portalOpen}
            </a>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16" data-testid="sub-loading">
            <Loader2 className="animate-spin text-primary" size={28} />
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 sm:p-8 mb-6 frame-pulse" data-testid="sub-card">
            {/* Plan */}
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div>
                <p className="flex items-center gap-2 text-sm text-primary/80 mb-1">
                  <Sparkles size={16} /> {L.currentPlan}
                </p>
                <p className="font-display text-3xl text-gradient-orange" data-testid="sub-plan">{planLabel(plan)}</p>
              </div>
              {sub && (
                <span
                  data-testid="sub-status"
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    canceling ? "border-amber-400/40 text-amber-300 bg-amber-400/10"
                    : sub.status === "active" ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
                    : "border-white/15 text-foreground/60 bg-white/5"}`}
                >
                  {canceling ? L.canceling : sub.status === "active" ? L.active : L.incomplete}
                </span>
              )}
            </div>

            {/* Subscription details */}
            {sub ? (
              <>
                <div className="grid sm:grid-cols-2 gap-5 mb-7">
                  <div>
                    <p className="text-xs text-foreground/45 mb-1">{L.price}</p>
                    <p className="text-foreground/85 text-lg" data-testid="sub-price">
                      {(sub.amount / 100).toFixed(0)} {sub.currency === "eur" ? "€" : (sub.currency || "").toUpperCase()}
                      <span className="text-foreground/45 text-sm"> {sub.interval === "year" ? L.perYear : L.perMonth}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground/45 mb-1">{canceling ? L.ends : L.renews}</p>
                    <p className="text-foreground/85 text-lg" data-testid="sub-period-end">{fmtDate(sub.current_period_end)}</p>
                  </div>
                </div>

                <div className="mb-5">
                  <Button
                    onClick={doPortal}
                    disabled={acting}
                    data-testid="sub-portal-btn"
                    className="btn-frost-orange w-full sm:w-auto"
                  >
                    {acting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CreditCard size={16} className="mr-2" />}
                    {L.updateCard}
                  </Button>
                </div>

                {canceling ? (
                  <div className="flex flex-col gap-3">
                    <p className="flex items-center gap-2 text-sm text-amber-300/90" data-testid="sub-canceling-note">
                      <XCircle size={16} /> {L.canceled}
                    </p>
                    <Button onClick={doResume} disabled={acting} data-testid="sub-resume-btn" className="btn-frost-orange w-full sm:w-auto">
                      {acting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                      {L.resume}
                    </Button>
                  </div>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" data-testid="sub-cancel-btn"
                        className="border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200 w-full sm:w-auto">
                        <XCircle size={16} className="mr-2" /> {L.cancel}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="glass border-white/10" data-testid="sub-cancel-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-foreground">{L.cancelTitle}</AlertDialogTitle>
                        <AlertDialogDescription className="text-foreground/60">{L.cancelDesc}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sub-cancel-no">{L.cancelNo}</AlertDialogCancel>
                        <AlertDialogAction data-testid="sub-cancel-yes" onClick={doCancel} disabled={acting}
                          className="bg-red-600 hover:bg-red-700 text-white">
                          {acting ? L.error === null ? "" : "…" : L.cancelOk}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            ) : (
              <div data-testid="sub-nosub">
                {data?.is_admin ? (
                  <p className="flex items-center gap-2 text-foreground/70"><ShieldCheck size={18} className="text-primary" /> {L.adminNote}</p>
                ) : data?.reward_active ? (
                  <p className="flex items-center gap-2 text-foreground/70"><CheckCircle2 size={18} className="text-emerald-400" /> {L.rewardNote}</p>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-foreground/65">{L.noSub}</p>
                    <Button onClick={() => navigate("/tarifs")} data-testid="sub-upgrade-btn" className="btn-frost-orange whitespace-nowrap">
                      <Sparkles size={16} className="mr-2" /> {L.upgrade}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <p className="flex items-center justify-center gap-2 text-xs text-foreground/35">
          <CreditCard size={13} /> {L.secure}
        </p>
      </motion.section>
    </div>
  );
}
